import TASPositivePayExceptionCheckForm from "../TASPositivePayExceptionCheckForm";

/**
 * @class TASPositivePayExceptionCheckFormUI
 * @classdesc Class definition for controlling a check
 * form which will then be transformed into a positive pay check.
 * 
 * @property {STRING} _id - The id of the check form.
 * @property {TASPositivePayExceptionCheckForm} _exceptionCheckForm - The exception check form data.
 * @property {BOOLEAN} _isEditing - Indicates whether the check form is currently being edited.
 * @property {OBJECT} _account - The account ui.
 * @property {OBJECT} _issuedDate - The date ui.
 * @property {OBJECT} _type - The type ui.
 * @property {OBJECT} _action - The action ui.
 * @property {OBJECT} _returnReason - The reason ui.
 * @property {OBJECT} _mobile - The mobile ui.
 */
export default class TASPositivePayExceptionCheckFormUI {
    constructor(record) {
        this.id = record.id;
        this.exceptionCheckForm = record.exceptionCheckForm;
        this.isEditing = record.isEditing;
        this.account = record.account;
        this.issuedDate = record.issuedDate;
        this.type = record.type;
        this.action = record.action;
        this.returnReason = record.returnReason;
        this.mobile = record.mobile;
    }

    /**
     * @name id
     * @instance
     * @property set {STRING} - sets the id
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set id(value) { this._id = value; }

    /**
     * @name exceptionCheckForm
     * @instance
     * @property set {OBJECT} - sets the exception check form
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set exceptionCheckForm(value) { this._exceptionCheckForm = value; }

    /**
     * @name isEditing
     * @instance
     * @property set {BOOLEAN} - sets whether the form is in edit mode
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set isEditing(value) { this._isEditing = value; }

    /**
     * @name account
     * @instance
     * @property set {STRING} - sets the account
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set account(value) { this._account = value; }

    /**
     * @name issuedDate
     * @instance
     * @property set {STRING} - sets the issued date
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set issuedDate(value) { this._issuedDate = value; }

    /**
     * @name type
     * @instance
     * @property set {STRING} - sets the exception type
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set type(value) { this._type = value; }

    /**
     * @name action
     * @instance
     * @property set {BOOLEAN} - sets whether changes have been saved.
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set action(val) {
        this._action = val;
    }

    /**
     * @name returnReason
     * @instance
     * @property set {BOOLEAN} - sets whether changes have been saved.
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set returnReason(val) {
        this._returnReason = val;
    }

    /**
     * @name mobile
     * @instance
     * @property set {BOOLEAN} - sets whether the mobile layout is enabled
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    set mobile(value) { this._mobile = value; }

    /**
     * @name id
     * @instance
     * @property get {STRING} - returns the id
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get id() { return this._id; }

    /**
     * @name exceptionCheckForm
     * @instance
     * @property get {OBJECT} - returns the exception check form
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get exceptionCheckForm() { return this._exceptionCheckForm; }

    /**
     * @name isEditing
     * @instance
     * @property get {BOOLEAN} - returns whether the form is in edit mode
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get isEditing() { return this._isEditing; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get account() { return this._account; }

    /**
     * @name issuedDate
     * @instance
     * @property get {STRING} - returns the issued date
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get issuedDate() { return this._issuedDate; }

    /**
     * @name type
     * @instance
     * @property get {STRING} - returns the exception type
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get type() { return this._type; }

    /**
     * @name action
     * @instance
     * @property get {STRING} - returns the account of the class.
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get action() {
        return this._action;
    }

    /**
     * @name returnReason
     * @instance
     * @property get {STRING} - returns the account of the class.
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get returnReason() {
        return this._returnReason;
    }

    /**
     * @name mobile
     * @instance
     * @property get {BOOLEAN} - returns whether the mobile layout is enabled
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get mobile() { return this._mobile; }

    get showReturnReasonSelect() {
        return this.exceptionCheckForm.action === "return";
    }

    get returnReasonHasError() {
        return this.exceptionCheckForm.action === "return"
        && this.exceptionCheckForm.returnReason === ""
        && this.exceptionCheckForm.didClickReturnReason === true;
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    get record() {
        return {
            id: this.id,
            exceptionCheckForm: this.exceptionCheckForm,
            isEditing: this.isEditing,
            account: this.account,
            issuedDate: this.issuedDate,
            type: this.type,
            action: this.action,
            returnReason: this.returnReason,
            mobile: this.mobile
        };
    }

    /**
     * @function reset
     * @instance
     * @property {VOID} - No parameters.
     * @description Returns an instance of the class with default values.
     * @memberof TASPositivePayExceptionCheckFormUI
     */
    reset() {
        const defaultVals = TASPositivePayExceptionCheckFormUI.defaultVals();
        return TASPositivePayExceptionCheckFormUI.init(defaultVals);
    }

    /** 
     * @function defaultVals   
     * @static
     * @property {VOID} - No parameters
     * @description Returns the default values of the class.
     * @memberof TASPositivePayExceptionCheckFormUI 
     */
    static defaultVals() {
        return { 
            id: "0",
            exceptionCheckForm: TASPositivePayExceptionCheckForm.init(),
            isEditing: true,
            account: { isActive: false },
            issuedDate: { isActive: false },
            type: { isActive: false },
            action: { isActive: false },
            returnReason: { isActive: false },
            mobile: { isActive: false }
        };
    }
    
    /** 
     * @function init   
     * @static
     * @property {OBJECT} - An object to initialize an instance of the class.
     * @description Returns an instance of the class.
     * @memberof TASPositivePayExceptionCheckFormUI 
     */
    static init(record) {
        const defaultVals = TASPositivePayExceptionCheckFormUI.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayExceptionCheckFormUI(vals);
    }
}